import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";

const API_BASE = "https://nissi-india.com/api/LRPrint"; 
// example backend: GET /api/lubricationRequest/details?requestId=123

const toStr = (v) => String(v ?? "").trim();

const LubricationReport = () => {
  const location = useLocation();
  const params = useParams();

  // ✅ supports both: /page/:requestId  AND  /page?requestId=xx
  const requestId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return toStr(params.requestId || qs.get("requestId"));
  }, [location.search, params.requestId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [header, setHeader] = useState({
    companyName: "Nissi Infotech",
    address: "3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India",
    phone: "9003744644",
    email: "infraconsoft-implementation@nissiinfotech.in",
    reportTitle: "Lubrication Request Report",
    monthTitle: "Lubrication Required Statement For The Month Of  Feb-2021",
  });

  const [lubricationData, setLubricationData] = useState([]);

  const handlePrint = () => window.print();

  // ✅ Fetch details by requestId
  useEffect(() => {
    const run = async () => {
      setError("");
      setLubricationData([]);

      if (!requestId) {
        setError("requestId not found in URL.");
        return;
      }

      setLoading(true);
      try {
        // ✅ expected response shape example:
        // {
        //   success:true,
        //   header:{ reportTitle, monthTitle, companyName, address, phone, email },
        //   rows:[ { sNo, description, unit, janClosingStock, receivedJan2021, ... } ]
        // }
        const res = await axios.get(`${API_BASE}/details`, {
          params: { requestId },
        });

        const data = res.data || {};
        if (!data.success) throw new Error(data.message || "Failed to load report");

        if (data.header) setHeader((prev) => ({ ...prev, ...data.header }));
        setLubricationData(Array.isArray(data.rows) ? data.rows : []);
      } catch (e) {
        setError(e?.message || "Something went wrong while loading the report.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [requestId]);

  // ✅ keep your empty rows logic
  const emptyRowCount = Math.max(0, 10);
  const startSno = (lubricationData?.length || 0) + 1;

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Print Button - Hidden during print */}
      <div className="print:hidden mb-6 text-center flex flex-col items-center gap-2">
        <button
          onClick={handlePrint}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          Print Report
        </button>

        {/* small status line */}
        <div className="text-xs">
          <span className="font-semibold">Request ID:</span>{" "}
          <span>{requestId || "-"}</span>
        </div>

        {loading && <div className="text-xs text-gray-700">Loading...</div>}
        {error && <div className="text-xs text-red-600">{error}</div>}
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto border-2 border-black p-4 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header Section */}
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            {/* Logo Section */}
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            {/* Company Info */}
            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">{header.companyName}</h1>
              <div className="text-xs leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>{header.address}</div>
                <div className="mt-1">
                  Phone No :{header.phone} &nbsp;&nbsp;&nbsp;&nbsp; {header.email}
                </div>
              </div>
            </div>
          </div>

          {/* Report Title */}
          <div className="text-center text-base font-bold my-4">
            {header.reportTitle}
          </div>

          {/* Sub Title */}
          <div className="text-center text-sm font-semibold mb-4 border border-black p-2 bg-gray-50">
            {header.monthTitle}
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-xs">
              <thead>
                <tr className="bg-white">
                  <th className="border border-black p-2 text-center font-bold">S.No</th>
                  <th className="border border-black p-2 text-center font-bold">Description</th>
                  <th className="border border-black p-2 text-center font-bold">Unit</th>
                  <th className="border border-black p-2 text-center font-bold">
                    Jan 2021<br />Closing Stock
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Received<br />Jan 2021
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Total<br />Jan 2021
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Consumption<br />Jan 2021
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Jan<br />Closing Stock
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Work Shop<br />Requirement
                  </th>
                  <th className="border border-black p-2 text-center font-bold">
                    Final<br />Requirement
                  </th>
                  <th className="border border-black p-2 text-center font-bold">Remarks</th>
                </tr>
              </thead>

              <tbody>
                {lubricationData.map((item, idx) => (
                  <tr key={item.sNo ?? idx}>
                    <td className="border border-black p-2 text-center">
                      {item.sNo ?? idx + 1}
                    </td>
                    <td className="border border-black p-2 text-left">{item.description}</td>
                    <td className="border border-black p-2 text-center">{item.unit}</td>
                    <td className="border border-black p-2 text-right">{item.janClosingStock}</td>
                    <td className="border border-black p-2 text-right">{item.receivedJan2021}</td>
                    <td className="border border-black p-2 text-right">{item.totalJan2021}</td>
                    <td className="border border-black p-2 text-right">{item.consumptionJan2021}</td>
                    <td className="border border-black p-2 text-right">{item.janClosingStock2021}</td>
                    <td className="border border-black p-2 text-right">{item.workShopRequirement}</td>
                    <td className="border border-black p-2 text-right">{item.finalRequirement}</td>
                    <td className="border border-black p-2 text-center">{item.remarks}</td>
                  </tr>
                ))}

                {/* Empty rows for spacing */}
                {[...Array(emptyRowCount)].map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td className="border border-black p-3 text-center">{startSno + i}</td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                    <td className="border border-black p-3"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="mt-4 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print\\:hidden {
            display: none !important;
          }

          .print\\:max-w-none {
            max-width: none !important;
          }

          .print\\:mx-0 {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .print\\:border-2 {
            border-width: 2px !important;
          }

          table {
            page-break-inside: auto;
          }

          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default LubricationReport;
